import type { MembershipRole } from "@safeplate/contracts";

export const IDLE_LOCK_SECONDS = 180;
export const IDLE_LOCK_MS = IDLE_LOCK_SECONDS * 1_000;

export interface ComputerPerson {
  addedAt: string;
  email: string;
  name: string;
  role: MembershipRole;
  staffProfileId: string | null;
  userId: string;
}

export interface RosterProfile {
  display_name: string;
  roles: readonly MembershipRole[];
  staff_profile_id: string;
}

export interface PersonTile {
  email: string | null;
  key: string;
  lastLoggedAt: string | null;
  method: "password" | "pin";
  name: string;
  role: MembershipRole;
  staffProfileId: string | null;
}

const roleRank: Record<MembershipRole, number> = {
  admin: 3,
  manager: 2,
  staff: 1,
};

export function peopleStorageKey(deviceId: string): string {
  return `safeplate_people.${deviceId}`;
}

function isRole(value: unknown): value is MembershipRole {
  return value === "admin" || value === "manager" || value === "staff";
}

export function parsePeople(raw: string | null): ComputerPerson[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const people: ComputerPerson[] = [];
  for (const entry of parsed) {
    if (typeof entry !== "object" || entry === null) continue;
    const candidate = entry as Record<string, unknown>;
    if (
      typeof candidate.userId !== "string" ||
      typeof candidate.name !== "string" ||
      typeof candidate.email !== "string" ||
      !isRole(candidate.role)
    ) {
      continue;
    }
    people.push({
      addedAt:
        typeof candidate.addedAt === "string"
          ? candidate.addedAt
          : new Date(0).toISOString(),
      email: candidate.email,
      name: candidate.name,
      role: candidate.role,
      staffProfileId:
        typeof candidate.staffProfileId === "string"
          ? candidate.staffProfileId
          : null,
      userId: candidate.userId,
    });
  }
  return people;
}

export function serialisePeople(people: readonly ComputerPerson[]): string {
  return JSON.stringify(people);
}

export function rememberPerson(
  people: readonly ComputerPerson[],
  person: ComputerPerson,
): ComputerPerson[] {
  const existing = people.find((entry) => entry.userId === person.userId);
  const next = existing
    ? people.map((entry) =>
        entry.userId === person.userId
          ? { ...person, addedAt: entry.addedAt }
          : entry,
      )
    : [...people, person];
  return next;
}

export function forgetPerson(
  people: readonly ComputerPerson[],
  userId: string,
): ComputerPerson[] {
  return people.filter((entry) => entry.userId !== userId);
}

export type LoggedHere = Readonly<Record<string, string>>;

function loggedTime(value: string | null): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

function validTime(value: string | undefined): string | null {
  return value && loggedTime(value) !== null ? value : null;
}

// Founder decision, 24 Oct 2026: whoever logged most recently on this
// computer comes first, then name. No due counts.
export function orderTiles(tiles: readonly PersonTile[]): PersonTile[] {
  return [...tiles].sort((a, b) => {
    const aTime = loggedTime(a.lastLoggedAt);
    const bTime = loggedTime(b.lastLoggedAt);
    if (aTime !== bTime) {
      if (aTime === null) return 1;
      if (bTime === null) return -1;
      return bTime - aTime;
    }
    return a.name.localeCompare(b.name);
  });
}

// People who hold a PIN are known to the server roster; everyone else who has
// signed in on this browser is known locally and signs in with a password.
export function mergePeople(
  local: readonly ComputerPerson[],
  roster: readonly RosterProfile[],
  loggedHere: LoggedHere = {},
): PersonTile[] {
  const pinned = new Map(
    roster.map((profile) => [profile.staff_profile_id, profile]),
  );
  const tiles: PersonTile[] = local.map((person) => {
    const profile =
      person.staffProfileId === null
        ? undefined
        : pinned.get(person.staffProfileId);
    const here =
      loggedHere[person.userId] ??
      (person.staffProfileId === null
        ? undefined
        : loggedHere[person.staffProfileId]);
    return {
      email: person.email,
      key: person.userId,
      lastLoggedAt: validTime(here),
      method: profile ? "pin" : "password",
      name: person.name,
      role: person.role,
      staffProfileId: person.staffProfileId,
    };
  });
  const known = new Set(local.map((person) => person.staffProfileId));
  for (const profile of roster) {
    if (known.has(profile.staff_profile_id)) continue;
    tiles.push({
      email: null,
      key: profile.staff_profile_id,
      lastLoggedAt: validTime(loggedHere[profile.staff_profile_id]),
      method: "pin",
      name: profile.display_name,
      role:
        [...profile.roles].sort((a, b) => roleRank[b] - roleRank[a])[0] ??
        "staff",
      staffProfileId: profile.staff_profile_id,
    });
  }
  return orderTiles(tiles);
}

export const roleLabels: Record<MembershipRole, string> = {
  admin: "Admin",
  manager: "Manager",
  staff: "Staff",
};

export function needsLockScreen(peopleCount: number): boolean {
  return peopleCount >= 2;
}

// The lock screen can serve a single person only when they hold a PIN; a lone
// password person has nothing to pick, so they go straight to sign-in.
export function lockScreenAvailable(tiles: readonly PersonTile[]): boolean {
  return (
    needsLockScreen(tiles.length) || tiles.some((tile) => tile.method === "pin")
  );
}

export interface SignOutPlan {
  destination: "/lock" | "/sign-in";
  remaining: PersonTile[];
  unbind: boolean;
}

export function planSignOut(
  tiles: readonly PersonTile[],
  userId: string,
  staffProfileIds: readonly string[],
): SignOutPlan {
  const own = new Set(staffProfileIds);
  const remaining = tiles.filter(
    (tile) =>
      tile.key !== userId &&
      !(tile.staffProfileId !== null && own.has(tile.staffProfileId)),
  );
  const destination = lockScreenAvailable(remaining) ? "/lock" : "/sign-in";
  return { destination, remaining, unbind: remaining.length === 0 };
}

export function idleLockDelayMs(peopleCount: number): number | null {
  return needsLockScreen(peopleCount) ? IDLE_LOCK_MS : null;
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
